import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, X as XIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import React from "react";

const ErrorModal = ({ isOpen, onClose, errorMessage, errorTitle }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    onClose();
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 10,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        duration: 0.5,
        bounce: 0.3,
        delayChildren: 0.1,
        staggerChildren: 0.05
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 10,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const iconAnimation = {
    scale: [1, 1.1, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      times: [0, 0.5, 1]
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 bg-black/40 z-[999] backdrop-blur-md"
            onClick={handleClose}
          />
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed inset-0 z-[1000] overflow-y-auto"
            role="dialog"
          >
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div 
                className="w-full max-w-md"
                variants={contentVariants}
              >
                <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-[0_20px_70px_-10px_rgba(0,0,0,0.3)] border border-white/20">
                  {/* Header */}
                  <div className="px-8 py-3 border-b border-gray-100/30">
                    <div className="flex items-center justify-between">
                      <motion.div 
                        variants={contentVariants}
                        className="flex items-center space-x-4"
                      >
                        <div className="relative">
                          <motion.div
                            animate={iconAnimation}
                          >
                            <AlertCircle className="h-7 w-7 text-red-500" />
                          </motion.div>
                          <div className="absolute -inset-1 bg-red-500/20 rounded-full blur-md" />
                        </div>
                        <motion.h2 
                          variants={contentVariants}
                          className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent"
                        >
                          {errorTitle || "Error"}
                        </motion.h2>
                      </motion.div>
                      <motion.button
                        whileHover={{ rotate: 90, scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close"
                      >
                        <XIcon className="h-6 w-6" />
                      </motion.button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="px-8 py-10">
                    <motion.div 
                      variants={contentVariants}
                      className="flex flex-col items-center text-center"
                    >
                      <div className="relative mb-8">
                        <div className="absolute inset-0 bg-red-500/20 rounded-full blur-xl" />
                        <motion.div
                          animate={{
                            scale: [1, 1.05, 1],
                            rotate: [0, 2, -2, 0]
                          }}
                          transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut",
                            times: [0, 0.33, 0.66, 1]
                          }}
                          className="h-28 w-28 bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center relative"
                        >
                          <AlertCircle className="h-14 w-14 text-red-500" />
                        </motion.div>
                      </div>
                      <div className="space-y-1">
                        {errorMessage.split(".").map((sentence, index) => (
                          <motion.p
                            key={index}
                            variants={contentVariants}
                            className="text-gray-600 text-sm leading-relaxed"
                          >
                            {sentence.trim()}
                          </motion.p>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  {/* Footer */}
                  <div className="px-8 py-6 bg-gray-50/50 rounded-b-2xl flex justify-end gap-4">
                    <motion.button
                      whileHover={{ scale: 1.02, translateY: -2 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      onClick={() => {
                        handleClose();
                        navigate("/senders");
                      }}
                      className="px-6 py-2.5 text-sm font-semibold text-white 
                               bg-gradient-to-r from-blue-500 to-blue-600
                               rounded-xl hover:shadow-lg hover:shadow-blue-500/25
                               transition-all duration-200"
                    >
                      Go to Senders
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02, translateY: -2 }}
                      whileTap={{ scale: 0.98 }}
                      transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      onClick={handleClose}
                      className="px-6 py-2.5 text-sm font-semibold text-white 
                               bg-gradient-to-r from-red-500 to-red-600
                               rounded-xl hover:shadow-lg hover:shadow-red-500/25
                               transition-all duration-200"
                    >
                      Close
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ErrorModal;
